import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  PresenceViewerDto,
  QaSessionDto,
  QuestionThreadDto,
} from '@lib/dto/question.dto'
import type { SigningSessionDto } from '@lib/dto/signing-session.dto'
import { signService } from '@/services/SignService'
import { qaKeys, signingKeys } from '@/hooks/queryKeys'
import { useQuestionRealtime } from '@/hooks/useQuestionRealtime'
import { requestErrorMessage } from '@/signing/SignChrome'
import { mergeQuestionEvent, upsertThread } from '@/signing/question-threads'
import type { DraftPin } from '@/signing/QuestionSidebar'
import type { DocumentSourceRoot } from '@/types/SignService.types'

type QuestionCache = {
  threads: QuestionThreadDto[]
  viewers: PresenceViewerDto[]
}

export function useQuestionSession({
  token,
  source,
  enabled,
  canAsk,
}: {
  token: string | undefined
  source: DocumentSourceRoot
  enabled: boolean
  canAsk: boolean
}): {
  draft: DraftPin | null
  selectedThreadId: string | null
  composer: string
  pending: boolean
  error: string | null
  onComposerChange: (value: string) => void
  onAskPin: ((pin: DraftPin) => void) | undefined
  onSelectThread: (id: string) => void
  onAsk: () => void
  onReply: () => void
  onCancelDraft: () => void
} {
  const queryClient = useQueryClient()
  const [draft, setDraft] = React.useState<DraftPin | null>(null)
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(null)
  const [composer, setComposer] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  function patch(updater: <T extends QuestionCache>(current: T) => T): void {
    if (!token) {
      return
    }
    // The source selects both the cache entry and the DTO stored in it.
    switch (source) {
      case 'sign':
        queryClient.setQueryData<SigningSessionDto>(signingKeys.session(token), (current) =>
          current === undefined ? current : updater(current),
        )
        return
      case 'qa':
        queryClient.setQueryData<QaSessionDto>(qaKeys.session(token), (current) =>
          current === undefined ? current : updater(current),
        )
        return
      default: {
        const unexpected: never = source
        throw new Error(`Unexpected document source ${String(unexpected)}`)
      }
    }
  }

  useQuestionRealtime({
    token,
    source,
    enabled,
    onEvent: (event) => {
      patch((current) => mergeQuestionEvent(current, event))
    },
  })

  const askMutation = useMutation({
    mutationFn: () => {
      if (!token || !draft) {
        throw new Error('Missing question')
      }
      return signService.createQuestion(token, {
        documentId: draft.documentId,
        page: draft.page,
        x: draft.x,
        y: draft.y,
        quote: draft.quote,
        body: composer.trim(),
      })
    },
    onSuccess: (thread) => {
      patch((current) => upsertThread(current, thread))
      setDraft(null)
      setComposer('')
      setSelectedThreadId(thread.id)
      setError(null)
    },
    onError: (err) => {
      setError(requestErrorMessage(err))
    },
  })

  const replyMutation = useMutation({
    mutationFn: () => {
      if (!token || !selectedThreadId) {
        throw new Error('Missing question')
      }
      return signService.replyQuestion(
        token,
        selectedThreadId,
        { body: composer.trim() },
        source,
      )
    },
    onSuccess: (thread) => {
      patch((current) => upsertThread(current, thread))
      setComposer('')
      setError(null)
    },
    onError: (err) => {
      setError(requestErrorMessage(err))
    },
  })

  return {
    draft,
    selectedThreadId,
    composer,
    pending: askMutation.isPending || replyMutation.isPending,
    error,
    onComposerChange: setComposer,
    onAskPin: canAsk
      ? (pin: DraftPin) => {
          setDraft(pin)
          setSelectedThreadId(null)
          setComposer('')
        }
      : undefined,
    onSelectThread: (id: string) => {
      setSelectedThreadId(id)
      setDraft(null)
      setComposer('')
    },
    onAsk: () => askMutation.mutate(),
    onReply: () => replyMutation.mutate(),
    onCancelDraft: () => {
      setDraft(null)
      setComposer('')
    },
  }
}
